import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Paper,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const LandingPage = ({ onLanguageChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: <TimelineIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '時系列表示',
      description: '会話を時系列で整理し、日付やタグでフィルタリングして過去の情報を素早く見つけられます。',
    },
    {
      icon: <SearchIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'キーワード検索',
      description: '会話内容からキーワード検索が可能。ハイライト表示で該当箇所を視覚的に確認できます。',
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'お気に入り管理',
      description: '重要な会話にお気に入りマークを付けて、専用ビューで一覧表示・管理できます。',
    },
    {
      icon: <ArticleIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'マークダウン表示',
      description: 'マークダウン形式の会話を美しくレンダリング。コードブロックやリストも見やすく表示されます。',
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'データインポート',
      description: 'JSONファイルから会話データを簡単にインポート。複数ファイルの一括処理にも対応。',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'ローカル保存',
      description: 'ブラウザのローカルストレージに保存。プライバシーを重視し、データは外部に送信されません。',
    },
  ];

  const handleGetStarted = () => {
    navigate('/app');
  };

  const handleSwitchToEnglish = () => {
    onLanguageChange('en');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Helmet>
        <html lang="ja" />
        <title>SynaptiQore - AIとの対話を知識に変える</title>
        <meta name="description" content="会話から生まれた洞察を整理・検索・再活用するナレッジツール" />
        <link rel="canonical" href="https://synaptiqore.contextaflex.com/" />
        <meta property="og:title" content="SynaptiQore - AIとの対話を知識に変える" />
        <meta property="og:description" content="会話から生まれた洞察を整理・検索・再活用するナレッジツール" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://synaptiqore.contextaflex.com/" />
        <meta property="og:image" content="https://synaptiqore.contextaflex.com/logo512.png" />
        <meta property="og:site_name" content="SynaptiQore" />
        <meta property="og:locale" content="ja_JP" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SynaptiQore - AIとの対話を知識に変える" />
        <meta name="twitter:description" content="会話から生まれた洞察を整理・検索・再活用するナレッジツール" />
        <meta name="twitter:image" content="https://synaptiqore.contextaflex.com/logo512.png" />
        <link rel="alternate" hrefLang="en" href="https://synaptiqore.contextaflex.com/?lang=en" />
        <link rel="alternate" hrefLang="ja" href="https://synaptiqore.contextaflex.com/" />
      </Helmet>
      {/* 言語切り替えボタンはヒーロー内に配置（重複防止） */}

      {/* ヒーロー部分: 白背景でクリーンなデザイン */}
      <Box sx={{ position: 'relative', backgroundColor: 'background.default', borderBottom: `1px solid ${theme.palette.divider}` }}>
        {/* 言語切り替えボタン */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSwitchToEnglish}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            English
          </Button>
        </Box>

        <Container maxWidth="lg">
          <Box sx={{ pt: 8, pb: 2, textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                mb: 1,
                fontSize: { xs: '2.5rem', md: '3.2rem' },
              }}
            >
              SynaptiQore
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                mb: 1,
              }}
            >
              シナプティコア
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: '500',
                mb: 4,
              }}
            >
              知識の接点を、思考の核心に
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* メインヒーロー部分: 白背景でコンテンツ重視 */}
      <Box sx={{ backgroundColor: 'background.default' }}>
        {/* ヒーローセクション */}
        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 6, md: 7 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 'bold',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                AIとの対話を
                <br />
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.primary.main,
                    px: 2,
                    py: 0.8,
                    borderRadius: 2,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    display: 'inline-block',
                  }}
                >
                  知識に変える
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                会話から生まれた洞察を整理・検索・再活用するナレッジツール
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    backgroundImage: theme.custom?.gradient,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    boxShadow: 3,
                    '&:hover': {
                      opacity: 0.9,
                    },
                  }}
                >
                  今すぐ始める
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  詳細を見る
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: 400,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <ArticleIcon sx={{ fontSize: 120, color: alpha(theme.palette.primary.main, 0.4) }} />
              </Box>
            </Grid>
          </Grid>
          </Box>
        </Container>
      </Box>

      {/* 機能セクション: 明るい面でコントラストを確保 */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 2,
              }}
            >
              主な機能
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                mb: 6,
              }}
            >
              AI会話を効率的に管理・活用する6つの機能
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 'bold',
                          mb: 2,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* CTA セクション: 明るい面で視認性を向上 */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 8 }}>
            <Box
              component={Paper}
              sx={{
                backgroundColor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                p: 6,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                情報の整理・検索・再活用を始めませんか？
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                }}
              >
                今すぐツールを体験して、情報の新しい価値を発見しましょう
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  backgroundImage: theme.custom?.gradient,
                  color: 'white',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  boxShadow: 2,
                  '&:hover': {
                    opacity: 0.95,
                  },
                }}
              >
                ツールを開始
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* フッター（ライト背景用） */}
      <Container maxWidth="lg">
        <Box sx={{ py: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            © 2024 SynaptiQore. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingPage;